import { supabase } from '@/lib/supabase'
import type { Profile, ProfileEditFormValues } from '@/types'

export const profileService = {

  async getByUsername(username: string): Promise<Profile | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('username', username.toLowerCase())
      .maybeSingle()

    if (error) throw new Error(error.message)
    return data
  },

  async getById(id: string): Promise<Profile | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .maybeSingle()

    if (error) throw new Error(error.message)
    return data
  },

  async update(id: string, values: ProfileEditFormValues): Promise<Profile> {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        username:   values.username.toLowerCase(),
        full_name:  values.full_name || null,
        bio:        values.bio || null,
        website:    values.website || null,
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw new Error(error.message)
    return data
  },

  async updateAvatar(userId: string, file: File): Promise<string> {
    const ext      = file.name.split('.').pop() ?? 'jpg'
    const filename = `${userId}/avatar.${ext}`

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filename, file, { upsert: true, contentType: file.type })

    if (uploadError) throw new Error(uploadError.message)

    const { data: urlData } = supabase.storage
      .from('avatars')
      .getPublicUrl(filename)

    const avatarUrl = urlData.publicUrl

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: avatarUrl })
      .eq('id', userId)

    if (updateError) throw new Error(updateError.message)

    return avatarUrl
  },

  async isUsernameTaken(username: string, excludeId?: string): Promise<boolean> {
    let query = supabase
      .from('profiles')
      .select('id')
      .eq('username', username.toLowerCase())

    if (excludeId) {
      query = query.neq('id', excludeId)
    }

    const { data } = await query.maybeSingle()
    return data !== null
  },
}
